/* English calendar for RFQ fields. Delegated listeners survive workspace renders. */
(function () {
  'use strict';
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const leap = year => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthDays = (year, month) => [31, leap(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  function parseISO(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
    if (!match) return null;
    const [, y, m, d] = match, year = Number(y), month = Number(m), day = Number(d);
    return year >= 1 && year <= 9999 && month >= 1 && month <= 12 && day >= 1 && day <= monthDays(year, month) ? { year, month, day } : null;
  }
  const formatISO = date => `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
  function weekday(date) {
    const value = new Date(0);
    value.setUTCFullYear(date.year, date.month - 1, date.day);
    value.setUTCHours(12, 0, 0, 0);
    return value.getUTCDay();
  }
  function moveDays(date, amount) {
    const value = new Date(0);
    value.setUTCFullYear(date.year, date.month - 1, date.day + amount);
    const year = value.getUTCFullYear();
    if (year < 1) return { year: 1, month: 1, day: 1 };
    if (year > 9999) return { year: 9999, month: 12, day: 31 };
    return { year, month: value.getUTCMonth() + 1, day: value.getUTCDate() };
  }
  function moveMonths(date, amount) {
    const index = Math.min(9999 * 12 - 1, Math.max(0, (date.year - 1) * 12 + date.month - 1 + amount));
    const year = Math.floor(index / 12) + 1, month = index % 12 + 1;
    return { year, month, day: Math.min(date.day, monthDays(year, month)) };
  }
  const describe = date => `${DAYS[weekday(date)]}, ${MONTHS[date.month - 1]} ${date.day}, ${date.year}`;
  // The CommonJS branch lets Node tests exercise these exact helpers without a DOM.
  if (typeof module === 'object' && module.exports) module.exports = { parseISO, formatISO, monthDays, weekday, moveDays, moveMonths, describe };
  if (typeof document === 'undefined') return;
  const listenerKey = Symbol.for('elyarchi.rfq.date-picker');
  if (globalThis[listenerKey]) return;
  globalThis[listenerKey] = true;
  let active = null, serial = 0;
  const today = () => { const now = new Date(); return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() }; };
  const inputFor = element => element?.closest?.('.rfq-date-picker')?.querySelector('input[data-rfq-date]');
  const minimum = input => parseISO(input?.dataset?.rfqMinDate);
  function close(restoreFocus = false) {
    if (!active) return;
    const previous = active;
    active = null;
    previous.popup.remove();
    previous.input.setAttribute('aria-expanded', 'false');
    previous.input.removeAttribute('aria-controls');
    previous.toggle?.setAttribute('aria-expanded', 'false');
    previous.toggle?.removeAttribute('aria-controls');
    if (restoreFocus && previous.input.isConnected) previous.input.focus({ preventScroll: true });
  }
  function position() {
    if (!active) return;
    if (!active.input.isConnected) { close(); return; }
    const rect = active.input.getBoundingClientRect();
    const width = Math.min(336, Math.max(240, window.innerWidth - 24));
    const popup = active.popup;
    popup.style.width = `${width}px`;
    popup.style.maxHeight = `${Math.max(180, window.innerHeight - 24)}px`;
    const height = popup.getBoundingClientRect().height;
    const below = rect.bottom + 8;
    const top = below + height <= window.innerHeight - 12 ? below : Math.max(12, rect.top - height - 8);
    popup.style.left = `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`;
    popup.style.top = `${Math.max(12, Math.min(top, window.innerHeight - height - 12))}px`;
  }
  function draw(focusDay = true) {
    if (!active) return;
    const { popup, cursor, selected } = active;
    const current = formatISO(today()), selectedISO = selected && formatISO(selected);
    const first = weekday({ ...cursor, day: 1 }), count = monthDays(cursor.year, cursor.month);
    const cells = [];
    for (let i = 0; i < Math.ceil((first + count) / 7) * 7; i++) {
      const day = i - first + 1;
      if (day < 1 || day > count) cells.push('<td role="gridcell"></td>');
      else {
        const date = { ...cursor, day }, iso = formatISO(date), selectedDay = iso === selectedISO;
        const disabled = minimum(active.input) && iso < formatISO(minimum(active.input));
        cells.push(`<td role="gridcell" aria-selected="${selectedDay}"><button type="button" data-rfq-day="${iso}" tabindex="${day === cursor.day ? 0 : -1}" aria-label="${describe(date)}"${iso === current ? ' aria-current="date"' : ''}${selectedDay ? ' class="is-selected"' : ''}${disabled ? ' disabled aria-disabled="true"' : ''}>${day}</button></td>`);
      }
    }
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(`<tr role="row">${cells.slice(i, i + 7).join('')}</tr>`);
    popup.innerHTML = `<div class="rfq-date-picker__heading"><h3 id="${popup.id}-title">Choose a date</h3><button type="button" data-rfq-close aria-label="Close calendar">&#215;</button></div>
      <div class="rfq-date-picker__navigation"><button type="button" data-rfq-month="-1" aria-label="Previous month"${cursor.year === 1 && cursor.month === 1 || minimum(active.input) && formatISO(cursor).slice(0, 7) <= formatISO(minimum(active.input)).slice(0, 7) ? ' disabled' : ''}>&#8249;</button><label><span class="rfq-date-picker__sr">Month</span><select data-rfq-month-select>${MONTHS.map((month, i) => `<option value="${i + 1}"${i + 1 === cursor.month ? ' selected' : ''}>${month}</option>`).join('')}</select></label><label><span class="rfq-date-picker__sr">Year</span><input data-rfq-year type="text" inputmode="numeric" maxlength="4" value="${cursor.year}" aria-describedby="${popup.id}-year-hint"></label><button type="button" data-rfq-month="1" aria-label="Next month"${cursor.year === 9999 && cursor.month === 12 ? ' disabled' : ''}>&#8250;</button></div>
      <span id="${popup.id}-year-hint" class="rfq-date-picker__year-error" hidden>Enter a year from 1 to 9999.</span><span class="rfq-date-picker__sr" aria-live="polite">${MONTHS[cursor.month - 1]} ${cursor.year}</span>
      <table role="grid" aria-label="${MONTHS[cursor.month - 1]} ${cursor.year}"><thead><tr role="row">${DAYS.map(day => `<th scope="col" role="columnheader" abbr="${day}">${day.slice(0, 3)}</th>`).join('')}</tr></thead><tbody>${rows.join('')}</tbody></table><p class="rfq-date-picker__hint">Use arrow keys to choose a date. Enter to select.</p><button type="button" class="rfq-date-picker__today" data-rfq-today>Go to today</button>${active.input.required?'':'<button type="button" class="rfq-date-picker__today" data-rfq-clear>Clear date</button>'}`;
    position();
    if (focusDay) popup.querySelector(`[data-rfq-day="${formatISO(cursor)}"]`)?.focus({ preventScroll: true });
  }
  function open(input) {
    if (input.disabled) return;
    if (active?.input === input) { close(true); return; }
    close();
    const selected = parseISO(input.value), min = minimum(input), cursor = selected && (!min || formatISO(selected) >= formatISO(min)) ? selected : min || today();
    const popup = document.createElement('div');
    popup.className = 'rfq-date-picker__popup';
    popup.id = `rfq-calendar-${++serial}`;
    popup.lang = 'en';
    popup.setAttribute('translate', 'no');
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-labelledby', `${popup.id}-title`);
    const toggle = input.closest('.rfq-date-picker').querySelector('[data-rfq-date-toggle]');
    active = { input, toggle, popup, selected, cursor };
    for (const trigger of [input, toggle].filter(Boolean)) {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'true');
      trigger.setAttribute('aria-controls', popup.id);
    }
    document.body.append(popup);
    draw();
  }
  function select(value) {
    if (!active || (value !== '' && (!parseISO(value) || minimum(active.input) && value < formatISO(minimum(active.input))))) return;
    const input = active.input, id = input.id;
    close();
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    const current = input.isConnected ? input : (id && document.getElementById(id));
    current?.focus({ preventScroll: true });
  }
  document.addEventListener('click', event => {
    const target = event.target;
    if (active?.popup.contains(target)) {
      const day = target.closest('[data-rfq-day]');
      if (day) { if (!day.disabled) select(day.dataset.rfqDay); return; }
      const month = target.closest('[data-rfq-month]');
      if (month) { const next = moveMonths(active.cursor, Number(month.dataset.rfqMonth)), min = minimum(active.input); if (!min || formatISO(next).slice(0, 7) >= formatISO(min).slice(0, 7)) { active.cursor = next; draw(); } return; }
      if (target.closest('[data-rfq-close]')) { close(true); return; }
      if (target.closest('[data-rfq-clear]')) { select(''); return; }
      if (target.closest('[data-rfq-today]')) { const min = minimum(active.input), current = today(); active.cursor = min && formatISO(current) < formatISO(min) ? min : current; draw(); }
      return;
    }
    const trigger = target.closest?.('[data-rfq-date-toggle],input[data-rfq-date]');
    if (trigger) { const input = inputFor(trigger); if (input) { event.preventDefault(); open(input); } }
  });
  document.addEventListener('pointerdown', event => {
    if (active && !active.popup.contains(event.target) && !active.input.closest('.rfq-date-picker').contains(event.target)) close();
  });
  document.addEventListener('focusin', event => {
    if (active && !active.popup.contains(event.target) && !active.input.closest('.rfq-date-picker').contains(event.target)) close();
  });
  document.addEventListener('change', event => {
    if (!active?.popup.contains(event.target)) return;
    if (event.target.matches('[data-rfq-month-select]')) {
      const next = { ...active.cursor, month: Number(event.target.value), day: Math.min(active.cursor.day, monthDays(active.cursor.year, Number(event.target.value))) };
      const min = minimum(active.input);
      active.cursor = min && formatISO(next) < formatISO(min) ? min : next;
      draw();
    } else if (event.target.matches('[data-rfq-year]')) {
      const year = Number(event.target.value), valid = /^\d{1,4}$/.test(event.target.value) && year >= 1 && year <= 9999;
      event.target.setAttribute('aria-invalid', String(!valid));
      active.popup.querySelector('.rfq-date-picker__year-error').hidden = valid;
      if (valid) { const next = { ...active.cursor, year, day: Math.min(active.cursor.day, monthDays(year, active.cursor.month)) }, min = minimum(active.input); active.cursor = min && formatISO(next) < formatISO(min) ? min : next; draw(); }
    }
  });
  document.addEventListener('keydown', event => {
    if (!active) {
      if (event.target.matches?.('input[data-rfq-date]') && ['Enter', ' ', 'ArrowDown'].includes(event.key)) { event.preventDefault(); open(event.target); }
      return;
    }
    if (event.key === 'Escape') { event.preventDefault(); close(true); return; }
    if (!active.popup.contains(event.target)) return;
    if (event.target.matches('[data-rfq-year]') && event.key === 'Enter') { event.preventDefault(); event.target.dispatchEvent(new Event('change', { bubbles: true })); return; }
    if (event.key === 'Tab') {
      const items = [...active.popup.querySelectorAll('button:not([disabled]):not([tabindex="-1"]),select,input')];
      if (event.shiftKey && event.target === items[0]) { event.preventDefault(); close(true); }
      else if (!event.shiftKey && event.target === items.at(-1)) { event.preventDefault(); close(true); }
      return;
    }
    if (!event.target.matches('[data-rfq-day]')) return;
    const date = parseISO(event.target.dataset.rfqDay);
    let next;
    if (event.key === 'ArrowLeft') next = moveDays(date, -1);
    if (event.key === 'ArrowRight') next = moveDays(date, 1);
    if (event.key === 'ArrowUp') next = moveDays(date, -7);
    if (event.key === 'ArrowDown') next = moveDays(date, 7);
    if (event.key === 'Home') next = moveDays(date, -weekday(date));
    if (event.key === 'End') next = moveDays(date, 6 - weekday(date));
    if (event.key === 'PageUp') next = moveMonths(date, event.shiftKey ? -12 : -1);
    if (event.key === 'PageDown') next = moveMonths(date, event.shiftKey ? 12 : 1);
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); select(formatISO(date)); return; }
    if (next) { event.preventDefault(); const min = minimum(active.input); active.cursor = min && formatISO(next) < formatISO(min) ? min : next; draw(); }
  });
  window.addEventListener('resize', position);
  document.addEventListener('scroll', position, true);
  if (typeof MutationObserver !== 'undefined') new MutationObserver(() => { if (active && !active.input.isConnected) close(); }).observe(document.body, { childList: true, subtree: true });
})();
